import { useCallback, useState } from "react";

import { useHouseholdConfigurations, useUpdateHouseholdConfiguration } from "@/api/rest/services/configurationService";
import Button from "@/components/common/Button";
import Spinner from "@/components/common/Spinner";
import { Switch } from "@/components/common/Switch";

interface HouseholdSettingsProps {
  buildingId: number;
  isDobuEmployee: boolean;
}

export function HouseholdSettings({ buildingId, isDobuEmployee }: HouseholdSettingsProps) {
  const { data: households, isLoading, error } = useHouseholdConfigurations(buildingId);
  const { mutateAsync: updateConfig } = useUpdateHouseholdConfiguration(buildingId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="w-6 h-6" />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 py-4">Failed to load household configurations.</div>;
  }

  if (!households || households.length === 0) {
    return <div className="text-gray-500 py-4">No households found.</div>;
  }

  return (
    <div className="space-y-4">
      {households.map((household) => (
        <HouseholdCard
          key={household.householdId}
          householdId={household.householdId}
          title={household.title}
          configurations={household.configurations}
          isDobuEmployee={isDobuEmployee}
          onUpdate={async (type, value) => {
            await updateConfig({
              householdId: household.householdId,
              request: { configurationType: type, configurationValue: value },
            });
          }}
        />
      ))}
    </div>
  );
}

interface HouseholdCardProps {
  householdId: number;
  title: string;
  configurations: Record<string, string>;
  isDobuEmployee: boolean;
  onUpdate: (type: string, value: string) => Promise<void>;
}

function HouseholdCard({ title, configurations, isDobuEmployee, onUpdate }: HouseholdCardProps) {
  const showCallLinks = configurations["SHOW_CALL_LINKS"] === "true";
  const isManagerHousehold = configurations["MANAGER_HOUSEHOLD"] === "true";
  const hausingRoomId = configurations["HAUSING_ROOM_ID"] || "";
  const [savingField, setSavingField] = useState<string | null>(null);

  const handleToggle = useCallback(
    async (type: string, currentValue: boolean) => {
      setSavingField(type);
      try {
        await onUpdate(type, (!currentValue).toString());
      } finally {
        setSavingField(null);
      }
    },
    [onUpdate],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h4 className="font-medium text-gray-900 mb-3">{title}</h4>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm text-gray-700">Show Call Links</label>
          <div className="flex items-center gap-2">
            {savingField === "SHOW_CALL_LINKS" && <Spinner className="w-4 h-4" />}
            <Switch
              checked={showCallLinks}
              onChange={() => handleToggle("SHOW_CALL_LINKS", showCallLinks)}
              disabled={savingField === "SHOW_CALL_LINKS"}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm text-gray-700">Manager Household (Haldur)</label>
          <div className="flex items-center gap-2">
            {savingField === "MANAGER_HOUSEHOLD" && <Spinner className="w-4 h-4" />}
            <Switch
              checked={isManagerHousehold}
              onChange={() => handleToggle("MANAGER_HOUSEHOLD", isManagerHousehold)}
              disabled={savingField === "MANAGER_HOUSEHOLD"}
            />
          </div>
        </div>

        {isDobuEmployee && (
          <HausingRoomIdField initialValue={hausingRoomId} onSave={(value) => onUpdate("HAUSING_ROOM_ID", value)} />
        )}
      </div>
    </div>
  );
}

function HausingRoomIdField({
  initialValue,
  onSave,
}: {
  initialValue: string;
  onSave: (value: string) => Promise<void>;
}) {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const isDirty = value !== initialValue;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-sm text-gray-700 whitespace-nowrap">Hausing Room ID</label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="form-input block w-40 rounded-md border-gray-300 shadow-sm focus:border-dobu-orange focus:ring-dobu-orange sm:text-sm"
          onBlur={() => isDirty && handleSave()}
        />
        {isDirty && (
          <Button onClick={handleSave} loading={saving} size="sm" className="rounded-md">
            Save
          </Button>
        )}
      </div>
    </div>
  );
}
